import { useState, useEffect } from "react";
import { api } from "@/App";
import { Users, Plus, Edit, Trash2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [specialites, setSpecialites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    nom: "",
    prenom: "",
    role: "collaborateur",
    specialite: "carrossier"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, specsRes] = await Promise.all([
        api.get("/users"),
        api.get("/specialites")
      ]);
      setUsers(usersRes.data);
      setSpecialites(specsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const handleOpenCreate = () => {
    setSelectedUser(null);
    setFormData({
      username: "",
      password: "",
      nom: "",
      prenom: "",
      role: "collaborateur",
      specialite: "carrossier"
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: "",
      nom: user.nom,
      prenom: user.prenom,
      role: user.role,
      specialite: user.specialite
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.nom || !formData.prenom || !formData.specialite) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    if (!selectedUser && (!formData.username || !formData.password)) {
      toast.error("Identifiant et mot de passe requis");
      return;
    }

    setSaving(true);
    try {
      if (selectedUser) {
        const updateData = {
          nom: formData.nom,
          prenom: formData.prenom,
          specialite: formData.specialite
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/users/${selectedUser.id}`, updateData);
        toast.success("Collaborateur modifié");
      } else {
        await api.post("/auth/register", formData);
        toast.success("Collaborateur créé");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d.message).join(", ") : (typeof detail === 'object' ? JSON.stringify(detail) : detail);
      toast.error(msg || "Erreur");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success("Collaborateur supprimé");
      setDeleteOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
    setSaving(false);
  };

  const getSpecialiteLabel = (specName) => {
    const spec = specialites.find(s => s.name === specName);
    return spec?.label || specName;
  };

  const getSpecialiteColor = (specName) => {
    const colors = {
      carrossier: "bg-orange-100 text-orange-700",
      peintre: "bg-blue-100 text-blue-700",
      mecanicien: "bg-emerald-100 text-emerald-700",
      admin: "bg-purple-100 text-purple-700"
    };
    return colors[specName] || "bg-slate-100 text-slate-700";
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in" data-testid="users-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Collaborateurs</h1>
          <p className="text-slate-600 mt-1">Gérez votre équipe</p>
        </div>
        <Button 
          onClick={handleOpenCreate}
          data-testid="add-user-btn"
          className="bg-red-600 hover:bg-red-700 text-white font-semibold h-11 px-6"
        >
          <UserPlus className="mr-2 h-5 w-5" />
          Ajouter
        </Button>
      </div>

      {/* Users Grid */}
      {users.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600">Aucun collaborateur</p>
            <Button onClick={handleOpenCreate} className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              Ajouter le premier
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((user) => (
            <Card 
              key={user.id} 
              className="border-slate-200 hover:border-red-300 transition-colors"
              data-testid={`user-card-${user.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                      user.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-red-100 text-red-700"
                    }`}>
                      {user.prenom?.[0]}{user.nom?.[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {user.prenom} {user.nom}
                      </p>
                      <p className="text-sm text-slate-500">@{user.username}</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex gap-2">
                    <Badge className={getSpecialiteColor(user.specialite)}>
                      {getSpecialiteLabel(user.specialite)}
                    </Badge>
                    {user.role === "admin" && (
                      <Badge className="bg-purple-100 text-purple-700">
                        Admin
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenEdit(user)}
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {user.role !== "admin" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => {
                          setSelectedUser(user);
                          setDeleteOpen(true);
                        }}
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedUser ? "Modifier le collaborateur" : "Nouveau collaborateur"}
            </DialogTitle>
            <DialogDescription>
              {selectedUser 
                ? "Modifiez les informations" 
                : "Créez un nouveau compte"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="prenom">Prénom *</Label>
                <Input
                  id="prenom"
                  data-testid="input-user-prenom"
                  value={formData.prenom}
                  onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nom">Nom *</Label>
                <Input
                  id="nom"
                  data-testid="input-user-nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                />
              </div>
            </div>
            
            {!selectedUser && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="username">Identifiant *</Label>
                  <Input
                    id="username"
                    data-testid="input-user-username"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe *</Label>
                  <Input
                    id="password"
                    data-testid="input-user-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                  />
                </div>
              </>
            )}
            
            {selectedUser && (
              <div className="space-y-2">
                <Label htmlFor="password">Nouveau mot de passe (optionnel)</Label>
                <Input
                  id="password"
                  data-testid="input-user-password"
                  type="password"
                  placeholder="Laisser vide pour ne pas changer"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="specialite">Spécialité *</Label>
              <Select
                value={formData.specialite}
                onValueChange={(v) => setFormData({...formData, specialite: v})}
              >
                <SelectTrigger data-testid="select-specialite">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {specialites.filter(s => s.name !== "admin").map((spec) => (
                    <SelectItem key={spec.id} value={spec.name}>
                      {spec.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="save-user-btn"
            >
              {saving ? <div className="spinner" /> : (selectedUser ? "Enregistrer" : "Créer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce collaborateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedUser && (
                <>Le compte de {selectedUser.prenom} {selectedUser.nom} sera définitivement supprimé.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              {saving ? <div className="spinner" /> : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
